"use client"

import type React from "react"
import { useState, useEffect, useRef } from "react"
import Papa from "papaparse"

interface School {
  Ärendenummer: string
  Datum: string
  Skola: string
  Typ: string
  Kommun: string
  Ärendemening: string
  "Full Description": string
  Students?: number
}

const ReportsSchoolSearch: React.FC = () => {
  const [schools, setSchools] = useState<School[]>([])
  const [filteredSchools, setFilteredSchools] = useState<School[]>([])
  const [selectedKommun, setSelectedKommun] = useState("")
  const [kommuner, setKommuner] = useState<string[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [isDropdownOpen, setIsDropdownOpen] = useState(false)
  const [searchQuery, setSearchQuery] = useState("")
  const dropdownRef = useRef<HTMLDivElement>(null)
  const searchInputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    const fetchData = async () => {
      try {
        setIsLoading(true)
        console.log("Fetching CSV data...")
        const response = await fetch("/data/riktad_tillsyn_grundskolor.csv")
        const csvData = await response.text()
        console.log("CSV data received, length:", csvData.length)

        Papa.parse(csvData, {
          header: true,
          skipEmptyLines: true,
          complete: (results) => {
            console.log("Papa parse complete, rows:", results.data.length)
            const parsedSchools = results.data as School[]

            // Filter out any invalid entries
            const validSchools = parsedSchools.filter(
              (school) => school && typeof school === "object" && school.Kommun && school.Skola,
            )
            console.log("Valid schools:", validSchools.length)

            setSchools(validSchools)

            // Extract unique kommuner
            const uniqueKommuner = [...new Set(validSchools.map((school) => school.Kommun))]
              .filter((kommun) => kommun && kommun.length > 0)
              .sort((a, b) => a.localeCompare(b, "sv"))

            console.log("Unique kommuner:", uniqueKommuner)
            setKommuner(uniqueKommuner)
            setIsLoading(false)
          },
          error: (error) => {
            console.error("Error parsing CSV:", error)
            setIsLoading(false)
          },
        })
      } catch (error) {
        console.error("Error fetching CSV:", error)
        setIsLoading(false)
      }
    }

    fetchData()
  }, [])

  useEffect(() => {
    if (selectedKommun) {
      const filtered = schools.filter((school) => school.Kommun === selectedKommun)
      setFilteredSchools(filtered.sort((a, b) => a.Skola.localeCompare(b.Skola, "sv")))
    } else {
      setFilteredSchools([])
    }
  }, [selectedKommun, schools])

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false)
        setSearchQuery("") // Clear search when closing dropdown
      }
    }

    document.addEventListener("mousedown", handleClickOutside)
    return () => {
      document.removeEventListener("mousedown", handleClickOutside)
    }
  }, [])

  // Focus search input when dropdown opens
  useEffect(() => {
    if (isDropdownOpen && searchInputRef.current) {
      searchInputRef.current.focus()
    }
  }, [isDropdownOpen])

  // Filter kommuner based on search query
  const filteredKommuner = kommuner.filter((kommun) => kommun.toLowerCase().includes(searchQuery.toLowerCase()))

  return (
    <div className="space-y-4">
      <div>
        <label htmlFor="kommunSearch" className="block text-sm font-medium text-gray-700 mb-2">
          Kommun:
        </label>
        {isLoading ? (
          <p>Laddar kommuner...</p>
        ) : (
          <div className="relative" ref={dropdownRef}>
            <div className="relative">
              <input
                id="kommunSearch"
                type="text"
                className="w-full md:w-96 bg-white border border-gray-300 rounded-md py-2 px-4 text-left shadow-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                placeholder="Sök kommun..."
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value)
                  setIsDropdownOpen(true)
                }}
                onFocus={() => setIsDropdownOpen(true)}
                aria-autocomplete="list"
                aria-controls="kommun-listbox"
                aria-haspopup="listbox"
              />
              {selectedKommun && searchQuery && (
                <button
                  type="button"
                  className="absolute inset-y-0 right-0 flex items-center pr-3"
                  onClick={() => {
                    setSearchQuery("")
                    setSelectedKommun("")
                    document.getElementById("kommunSearch")?.focus()
                  }}
                >
                  <svg
                    className="h-5 w-5 text-gray-400 hover:text-gray-500"
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                    aria-hidden="true"
                  >
                    <path
                      fillRule="evenodd"
                      d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                      clipRule="evenodd"
                    />
                  </svg>
                </button>
              )}
            </div>

            {isDropdownOpen && (
              <div
                id="kommun-listbox"
                role="listbox"
                className="absolute z-10 mt-1 w-full md:w-96 bg-white shadow-lg rounded-md py-1 text-base ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm max-h-60 overflow-auto"
              >
                {filteredKommuner.length > 0 ? (
                  filteredKommuner.map((kommun) => (
                    <div
                      key={kommun}
                      role="option"
                      aria-selected={selectedKommun === kommun}
                      className={`cursor-pointer select-none relative py-2 pl-3 pr-9 hover:bg-blue-50 ${
                        selectedKommun === kommun ? "bg-blue-100 text-blue-900" : "text-gray-900"
                      }`}
                      onClick={() => {
                        setSelectedKommun(kommun)
                        setSearchQuery(kommun)
                        setIsDropdownOpen(false)
                      }}
                    >
                      {kommun}
                    </div>
                  ))
                ) : (
                  <div className="py-2 pl-3 pr-9 text-gray-500 italic">Inga kommuner hittades</div>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {filteredSchools.length === 0 ? (
        selectedKommun ? (
          <p>Inga skolor hittades i den valda kommunen.</p>
        ) : (
          <p>Vänligen välj kommun för att se resultat.</p>
        )
      ) : (
        <div className="overflow-x-auto mt-4">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th
                  scope="col"
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                >
                  Skola
                </th>
                <th
                  scope="col"
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                >
                  Antal elever
                </th>
                <th
                  scope="col"
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                >
                  Ärendenummer
                </th>
                <th
                  scope="col"
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                >
                  Datum
                </th>
                <th
                  scope="col"
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                >
                  Ärendemening
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredSchools.map((school, index) => (
                <tr key={index}>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{school.Skola}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {school.Students && !isNaN(Number(school.Students))
                      ? `ca. ${Math.round(Number(school.Students) / 10) * 10}`
                      : "Ingen data"}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{school.Ärendenummer}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{school.Datum}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{school.Ärendemening}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}

export default ReportsSchoolSearch

// Update selected kommun when exact match is found

